import { useLayoutEffect, useMemo, useRef } from 'react';
import { getData } from '../backend';
import { useModal } from '../contexts';
import Modal from './Modal';
import useSWR, { KeyedMutator } from "swr";
import hsObjectTypes, { HubspotObject, HubspotObjectList } from '../hsObjectTypes';
import useUser from '../hooks/useUser';
import PayWallModalPro from './PayWallModalPro';
import WorkflowModalHubspotPropertiesDetail from './WorkflowModalHubspotPropertiesDetail';
import { Workflow } from '../interfaces';
import { CashIcon, CubeTransparentIcon, OfficeBuildingIcon, TicketIcon, UserGroupIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';

interface Props {
  workflow: Workflow;
  refreshWorkflow: KeyedMutator<Workflow>;
  onClose: () => void;
}

function WorkflowModalHubspotPropertiesCaptureList(props: Props) {
  const { user } = useUser();
  const { openModal, closeModal } = useModal();

  const { t } = useTranslation();

  const { data } = useSWR<Array<HubspotObjectList>>(`/hubspot/object-types/`, getData, { fallbackData: hsObjectTypes, shouldRetryOnError: false });

  const scollRef = useRef<HTMLDivElement>(null);
  const primaryObjectTypeId = props.workflow.source?.hubspotConfig?.hubspotObjectTypeId || "0-3"; // Default to Deal if not set

  useLayoutEffect(() => {
    // Hack to set modal back to top after it has been previously loaded
    setTimeout(() => scollRef.current?.scrollTo({ top: 0, behavior: "auto" }), 0)
  }, [scollRef]);

  const objectMap: Record<string, HubspotObject> = useMemo(() => {
    const map: Record<string, HubspotObject> = {};
    for (const objectList of (data || []))
      for (const obj of objectList.objects)
        map[obj.id] = obj;

    return map;
  }, [data]);

  function handleSelectObject(objectId: string, isCustomObject: boolean = false) {
    if (isCustomObject && !user?.subscription?.featureHubspotCustomObjects)
      return openModal(<PayWallModalPro />);

    openModal(<WorkflowModalHubspotPropertiesDetail objectTypeId={objectId} workflow={props.workflow} refreshWorkflow={props.refreshWorkflow} onClose={props.onClose} captureProperties />)
  }

  return (
    <Modal
      title={"Capture Fields"}
      subtitle={"Select the HubSpot properties you want to capture from the user."}
      secondaryButtonProps={{ text: "Cancel", onClick: closeModal }}
      onClose={props.onClose}
    >
      <div ref={scollRef} className='flex flex-col overflow-y-scroll max-h-[400px] gap-2 pr-8'>
        <div className='w-full flex gap-4'>
          <div className='flex flex-col items-center justify-center gap-2 border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 h-32 relative' onClick={() => handleSelectObject(primaryObjectTypeId)}>
            {primaryObjectTypeId === "0-1" && <UserGroupIcon className='w-8 h-8' />}
            {primaryObjectTypeId === "0-2" && <OfficeBuildingIcon className='w-8 h-8' />}
            {primaryObjectTypeId === "0-3" && <CashIcon className='w-8 h-8' />}
            {primaryObjectTypeId === "0-5" && <TicketIcon className='w-8 h-8' />}
            {!["0-1", "0-2", "0-3", "0-5"].includes(primaryObjectTypeId!) && <CubeTransparentIcon className='w-8 h-8' />}
            {objectMap[primaryObjectTypeId]?.name || "Primary Object"}
          </div>
          {primaryObjectTypeId !== "0-1" &&
            <div className='flex flex-col items-center justify-center gap-2 border rounded w-full p-4 hover:border-blue cursor-pointer font-gilroy font-semibold text-gray-600 h-32 relative' onClick={() => handleSelectObject("0-1")}>
              <UserGroupIcon className='w-8 h-8' />
              {t("Contact (Recipient)")}
            </div>
          }
        </div>
      </div>
    </Modal>
  );
}

export default WorkflowModalHubspotPropertiesCaptureList;
